import { request } from './client.js';

export type AdminAccountSort = 'name' | 'email' | 'createdAt' | 'books' | 'status';
export type AdminAccountDirection = 'asc' | 'desc';
export type AdminBookStatus = 'draft' | 'generating' | 'ready' | 'archived';

export interface AdminBookReplacementLimitDto {
  readonly bookId: string;
  readonly used: number;
  readonly included: number;
  readonly extra: number;
  readonly remaining: number;
  readonly unlimited: boolean;
}

export interface AdminAccountDto {
  readonly id: string;
  readonly email: string;
  readonly name: string;
  readonly role: 'user' | 'admin';
  readonly enabled: boolean;
  readonly disabledAt: string | null;
  readonly emailVerified: boolean;
  readonly createdAt: string;
  readonly bookCounts: Readonly<Record<AdminBookStatus, number>> & { readonly total: number };
  readonly books: readonly {
    readonly id: string;
    readonly title: string;
    readonly status: AdminBookStatus;
    readonly updatedAt: string;
  }[];
}

export const adminAccountsApi = {
  list: (sort: AdminAccountSort, direction: AdminAccountDirection) => {
    const params = new URLSearchParams({ sort, direction });
    return request<{ items: readonly AdminAccountDto[]; total: number }>(
      `/admin/accounts?${params.toString()}`,
    );
  },
  setEnabled: (id: string, enabled: boolean) =>
    request<{ id: string; enabled: boolean; disabledAt: string | null }>(
      `/admin/accounts/${id}/status`,
      { method: 'PATCH', body: { enabled } },
    ),
  registrationStatus: () =>
    request<{ registrationEnabled: boolean }>('/admin/accounts/registration'),
  setRegistrationEnabled: (registrationEnabled: boolean) =>
    request<{ registrationEnabled: boolean }>('/admin/accounts/registration', {
      method: 'PATCH',
      body: { registrationEnabled },
    }),
  economyModeStatus: () => request<{ economyModeEnabled: boolean }>('/admin/accounts/economy-mode'),
  setEconomyModeEnabled: (economyModeEnabled: boolean) =>
    request<{ economyModeEnabled: boolean }>('/admin/accounts/economy-mode', {
      method: 'PATCH',
      body: { economyModeEnabled },
    }),
  unapplyStory: (bookId: string) =>
    request<{
      bookId: string;
      draftId: string | null;
      reset: true;
      removed: { scenes: number; sceneVariants: number; coverVariants: number; exports: number };
    }>(`/admin/books/${bookId}/story/unapply`, { method: 'POST' }),
  bookReplacementLimit: (bookId: string) =>
    request<AdminBookReplacementLimitDto>(`/admin/books/${bookId}/replacements`),
  setBookReplacementLimit: (bookId: string, unlimited: boolean) =>
    request<AdminBookReplacementLimitDto>(`/admin/books/${bookId}/replacements`, {
      method: 'PATCH',
      body: { unlimited },
    }),
  create: (body: {
    email: string;
    name: string;
    password: string;
    role: 'user' | 'admin';
    emailVerified: boolean;
  }) => request<AdminAccountDto>('/admin/accounts', { method: 'POST', body }),
};
